import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Alert from '@cloudscape-design/components/alert';
import { signUp, signIn, confirmSignUp, resendConfirmationCode } from './auth';

interface AuthModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
}

type AuthMode = 'signin' | 'signup' | 'confirm';

export default function AuthModal({ visible, onDismiss, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSignIn = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await signIn(email, password);
      onSuccess();
      resetForm();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';

      // Handle "User is not confirmed" — resend verification code and switch to confirm mode
      if (
        errorMessage.includes('User is not confirmed') ||
        errorMessage.includes('UserNotConfirmedException')
      ) {
        try {
          await resendConfirmationCode(email);
          setSuccessMessage('A new verification code has been sent to your email.');
        } catch (resendErr: any) {
          setError(`Account not verified. Could not resend code: ${resendErr.message}`);
        }
        setMode('confirm');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await signUp(email, password);
      setMode('confirm');
      setSuccessMessage('A verification code has been sent to your email.');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign up';

      // Handle "User already exists" — auto-resend verification code and switch to confirm
      if (
        errorMessage.includes('User already exists') ||
        errorMessage.includes('UsernameExistsException')
      ) {
        try {
          await resendConfirmationCode(email);
          setSuccessMessage('A verification code has been sent to your email.');
          setMode('confirm');
        } catch (resendErr: any) {
          // If resend fails, the user is likely already confirmed — direct to sign in
          setError('An account with this email already exists. Please sign in instead.');
        }
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await confirmSignUp(email, confirmCode);
      await signIn(email, password);
      onSuccess();
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to confirm account');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await resendConfirmationCode(email);
      setSuccessMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendAndSwitchToConfirm = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await resendConfirmationCode(email);
      setSuccessMessage('Verification code sent! Check your email.');
      setMode('confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmCode('');
    setMode('signin');
    setError('');
    setSuccessMessage('');
  };

  const handleDismiss = () => {
    resetForm();
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Confirm Account'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={mode === 'signin' ? handleSignIn : mode === 'signup' ? handleSignUp : handleConfirm}
              loading={loading}
            >
              {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Confirm'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert type="success" dismissible onDismiss={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {mode === 'confirm' ? (
          <>
            <Alert type="info">
              A verification code has been sent to <strong>{email}</strong>. Please enter it below.
            </Alert>
            <FormField label="Verification Code">
              <Input
                value={confirmCode}
                onChange={({ detail }) => setConfirmCode(detail.value)}
                placeholder="Enter 6-digit code"
              />
            </FormField>
            <Box textAlign="center">
              Didn't receive the code?{' '}
              <Button variant="inline-link" onClick={handleResendCode} loading={loading}>
                Resend code
              </Button>
            </Box>
          </>
        ) : (
          <>
            <FormField label="Email">
              <Input
                value={email}
                onChange={({ detail }) => setEmail(detail.value)}
                type="email"
                placeholder="your@email.com"
              />
            </FormField>

            <FormField label="Password">
              <Input
                value={password}
                onChange={({ detail }) => setPassword(detail.value)}
                type="password"
                placeholder="Enter password"
              />
            </FormField>

            {mode === 'signin' ? (
              <Box textAlign="center">
                Don't have an account?{' '}
                <Button variant="inline-link" onClick={() => { setMode('signup'); setError(''); setSuccessMessage(''); }}>
                  Sign up
                </Button>
              </Box>
            ) : (
              <Box textAlign="center">
                Already have an account?{' '}
                <Button variant="inline-link" onClick={() => { setMode('signin'); setError(''); setSuccessMessage(''); }}>
                  Sign in
                </Button>
              </Box>
            )}
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
}
